/**
 * Wizard 1 Flow Controller
 * 
 * Main controller that orchestrates the entire buying flow
 * Initializes router, state, storage, and handles step navigation
 */

/** Quiz steps only (not flavors / plan / bulk / profile) — reset window scroll so offer bar stays visible */
const WIZARD_1_QUIZ_STEPS_SCROLL_TOP = new Set([
  'name',
  'spayed',
  'age',
  'breed',
  'weight',
  'composition',
  'activity',
  'allergies',
  'snacks',
  'email',
  'calculation'
]);

class Wizard1Flow {
  constructor() {
    this.config = null;
    this.router = null;
    this.state = null;
    this.storage = null;
    this.shopifyAPI = null;
    this.initialized = false;
  }

  /**
   * Initialize the flow
   * @returns {Promise<void>}
   */
  async init() {
    if (this.initialized) {
      return;
    }

    try {
      // Load flow configuration
      // Note: flow-config.js must be loaded before this file
      if (typeof flowConfig === 'undefined') {
        throw new Error('flow-config.js must be loaded before wizard-1-flow.js');
      }
      this.config = await flowConfig.getDefaultFlowConfig();

      // Initialize components
      // Note: All component classes must be loaded before this file
      if (typeof PetFlowState === 'undefined' || 
          typeof PetFlowStorage === 'undefined' || 
          typeof FlowRouter === 'undefined' || 
          typeof ShopifyAPI === 'undefined') {
        throw new Error('All component files must be loaded before wizard-1-flow.js');
      }

      // StepHandler will be loaded dynamically

      this.state = new PetFlowState(this.config.name);
      this.storage = new PetFlowStorage(this.config.name);
      this.router = new FlowRouter(this.config);
      this.shopifyAPI = new ShopifyAPI(this.config);

      // Initialize calculation engine
      if (typeof CalculationEngine === 'undefined') {
        throw new Error('wizard-1-calculations.js must be loaded before wizard-1-flow.js');
      }
      this.calculationEngine = new CalculationEngine();

      // Initialize step handler
      if (typeof StepHandler === 'undefined') {
        throw new Error('wizard-1-steps.js must be loaded before wizard-1-flow.js');
      }
      this.stepHandler = new StepHandler(this);
      await this.stepHandler.init();

      // Initialize analytics (optional, if script is loaded)
      if (typeof Wizard1Analytics !== 'undefined') {
        this.analytics = new Wizard1Analytics(this);
      } else {
        this.analytics = null;
      }

      // Initialize Klaviyo event manager (optional, if script is loaded)
      // Use window.__wizard1BackendApiUrl directly (same as ShopifyAPI)
      const backendApiUrl = window.__wizard1BackendApiUrl;
      if (typeof KlaviyoEventManager !== 'undefined' && backendApiUrl) {
        this.klaviyoManager = new KlaviyoEventManager(backendApiUrl, this.config);
      } else {
        this.klaviyoManager = null;
      }

      // Set up router listener before starting router,
      // so the initial route change is observed by the flow
      this.router.onRouteChange((step, params) => {
        this.handleRouteChange(step, params);
      });

      // Detect logged-in customer before loading saved state
      await this.detectLoggedInCustomer();

      // Load saved state BEFORE router initialization to prevent empty state from overwriting localStorage
      // This ensures state is populated before any rendering happens
      await this.loadSavedState();

      // Expose flow globally for storage access and router
      window.wizard1Flow = this;

      // Auto-redirect to resume step when visiting root URL with saved state
      // Source of truth: Shopify metaobject resume_step (set by loadPetDataFromMetaobject)
      // Fallback: state.currentStep from localStorage when no metaobject
      // Sanity check: if step not in routes, fall back to 'name'
      const path = this.router.getCurrentPath();
      const stepFromPath = this.router.getStepFromPath(path);
      if (this.router.isRootUrl(path, stepFromPath)) {
        const petData = this.state.getPetData();
        const hasLoadedData = petData.name || petData.gender || petData.ageGroup || petData.breed ||
          petData.weight !== null || this.state.customerId;
        if (hasLoadedData) {
          const resumeStep = this.state.getCurrentStep() || 'name';
          const targetStep = this.router.getPathForStep(resumeStep) ? resumeStep : 'name';
          const stepToPath = this.router.getPathForStep(targetStep);
          if (stepToPath) {
            const localePrefix = this.router.getLocalePrefix();
            const fullPath = localePrefix ? localePrefix + stepToPath : stepToPath;
            window.history.replaceState({}, '', fullPath);
            this.state.setCurrentStep(targetStep);
          }
        }
      }

      // Start router and process initial route AFTER state is loaded
      // Router will use the loaded state (or URL if no saved state exists)
      this.router.init();

      // Set up auto-save
      this.setupAutoSave();

      this.initialized = true;
      
      // Update page title based on current step
      this.updatePageTitle();
      
      console.log('Wizard 1 Flow initialized');
    } catch (error) {
      console.error('Error initializing flow:', error);
      throw error;
    }
  }

  /**
   * Detect if customer is logged in via Storefront API
   * Note: Returns 403 for anonymous users, which is expected behavior.
   * @returns {Promise<void>}
   */
  async detectLoggedInCustomer() {
    try {
      // FIX: Don't request email field - it requires customerAccessToken for anonymous users
      // and causes "missing required arguments" error. We only need the ID to detect if logged in.
      const query = `
        query {
          customer {
            id
            # Removed email field due to missingRequiredArguments error for anonymous users
          }
        }
      `;

      const data = await this.shopifyAPI.executeGraphQL(query);

      // Handle null return (anonymous user)
      if (data === null) {
        return;
      }

      if (data && data.customer && data.customer.id) {
        this.state.customerId = data.customer.id;
      }
    } catch (error) {
      // Customer not logged in or error - this is fine, continue as anonymous
      // GraphQL errors for missing customerAccessToken are expected for anonymous users
      // Check if it's a GraphQL error about missing arguments (anonymous user)
      const isAnonymousUser = error.message && (
        error.message.includes('customerAccessToken') ||
        error.message.includes('missing required arguments') ||
        error.message.includes('ACCESS_DENIED')
      );
      
      if (!(error.status === 403 || error.statusCode === 403 || (error.message && error.message.includes('403')) || isAnonymousUser)) {
        // For other errors, log as warning (not error) since this is not critical
        console.warn('Error detecting logged-in customer:', error.message);
      }
    }
  }

  /**
   * Load saved state from localStorage or Shopify
   * @returns {Promise<void>}
   */
  async loadSavedState() {
    // Priority order:
    // 1. Check URL for ?pet= params (resume from abandoned quiz email link)
    // 2. If logged in, fetch Pet Metaobjects from Shopify
    // 3. If Pet Metaobjects exist, load the most recent one
    // 4. If no Shopify pets but localStorage exists, load from localStorage
    // 5. If neither exists, start fresh

    const urlParams = new URLSearchParams(window.location.search);
    const petIds = urlParams.getAll('pet');
    if (petIds.length > 0 && this.shopifyAPI) {
      try {
        const currentPetId = petIds[petIds.length - 1];
        const metaobject = await this.shopifyAPI.getPetMetaobjectById(currentPetId);
        if (metaobject) {
          this.stepHandler.loadPetDataFromMetaobject(metaobject);
          if (metaobject.cartItems && metaobject.cartItems.length > 0) {
            try {
              await this.shopifyAPI.addToCart(metaobject.cartItems);
            } catch (e) {
              console.warn('Failed to re-add cart on resume (non-blocking):', e.message);
            }
          }
          return;
        }
      } catch (error) {
        console.warn('Resume from ?pet= failed, falling back:', error.message);
      }
    }

    if (this.state.customerId) {
      // Customer is logged in - check Shopify first
      try {
        const shopifyPets = await this.shopifyAPI.getAllPetMetaobjects(this.state.customerId);
        
        if (shopifyPets && shopifyPets.length > 0) {
          // Load the most recent pet (first in array, or could sort by creation date)
          const mostRecentPet = shopifyPets[0];
          
          if (mostRecentPet && mostRecentPet.id) {
            // Load pet data from Metaobject
            const metaobject = await this.shopifyAPI.getPetMetaobjectById(mostRecentPet.id);
            
            if (metaobject) {
              // Load data into state
              this.stepHandler.loadPetDataFromMetaobject(metaobject);
              
              // State is now loaded, router.init() will handle navigation
              // The router will use the loaded step from state
              return; // Don't check localStorage if Shopify data loaded
            }
          }
        }
      } catch (error) {
        console.error('Error loading from Shopify:', error);
        // Fall through to localStorage check
      }
    }

    // Fallback: Try to load from localStorage
    const savedState = this.storage.loadFromLocalStorage();
    
    if (savedState) {
      // Invalidate variantPricing when country has changed (currency differs)
      const currentCountry = (typeof window !== 'undefined' && window.__wizard1CurrentCountry) || '';
      const savedCountry = savedState._savedCountry || '';
      if (savedCountry && currentCountry && savedCountry.toUpperCase() !== currentCountry.toUpperCase()) {
        delete savedState.variantPricing;
      }
      delete savedState._savedCountry; // Don't merge metadata into state
      this.state.loadState(savedState);
      // State is now loaded, router.init() will handle navigation based on URL or loaded step
    }

    // Resume pending Klaviyo events if any
    if (this.klaviyoManager && this.state.petData && this.state.petData.ownerEmail) {
      try {
        this.klaviyoManager.resumePendingEvent(this.state.petData.ownerEmail);
      } catch (e) {
        console.warn('Failed to resume pending Klaviyo event:', e);
      }
    }
  }

  /**
   * Set up auto-save functionality
   */
  setupAutoSave() {
    // Auto-save on state changes
    // This will be called whenever state is updated
    // For now, we'll save on step changes
  }

  /**
   * After navigating to a quiz step, scroll the window to top so header + offer bar are visible.
   * Scroll only; does not move focus (a11y). Skips flavors / plan / bulk / profile.
   * @param {string} step
   */
  scrollWindowToTopIfQuizStep(step) {
    if (!WIZARD_1_QUIZ_STEPS_SCROLL_TOP.has(step)) return;
    const scrollTop = () => {
      try {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      } catch (e) {
        window.scrollTo(0, 0);
      }
    };
    // Double rAF: run after layout/paint from the new step; setTimeout(0): catch late iOS / focus reconciliation
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollTop();
        setTimeout(scrollTop, 0);
      });
    });
  }

  /**
   * Handle route change
   * @param {string} step - Step identifier
   * @param {Object} params - Route parameters
   */
  async handleRouteChange(step, params) {
    // Klaviyo resume link (property wizard_1_resume_url): pathname + origin per session
    // Use full pathname (including locale) so resume links keep users in their locale
    if (this.klaviyoManager && this.router) {
      const path = window.location.pathname;
      this.klaviyoManager.setLastOriginPath(window.location.origin, path);
    }

    // Update state with current step
    this.state.setCurrentStep(step);

    // Redirect to flavors if accessing plan/bulk steps without selected flavors
    if (step === 'plan' || step === 'bulk') {
      const selectedFlavors = this.state.selectedFlavors || [];
      if (selectedFlavors.length === 0) {
        // Redirect to flavors step immediately (replace=true to avoid adding to history)
        this.router.navigate('flavors', {}, true);
        return; // Exit early to prevent rendering
      }
    }

    // Render step (step handler will handle special cases like profile)
    await this.renderStep(step, params);

    this.scrollWindowToTopIfQuizStep(step);

    // Track step view in analytics (if available)
    if (this.analytics && typeof this.analytics.trackStepView === 'function') {
      try {
        this.analytics.trackStepView(step);
      } catch (e) {
        console.warn('Wizard analytics step tracking failed:', e);
      }
    }

    // Auto-save state (localStorage always, Shopify debounced)
    // Only save if state has actual data (not empty/default state)
    // This prevents overwriting saved state with empty state during initialization
    const state = this.state.getState();
    const hasData = state.petData.name || 
                   state.petData.gender || 
                   state.customerId || 
                   state.petMetaobjectId ||
                   state.petData.ageGroup ||
                   state.petData.breed ||
                   state.petData.weight !== null;
    
    if (hasData || this.initialized) {
      // Only auto-save if we have data OR if flow is fully initialized
      // This prevents empty state from overwriting saved state during init
      await this.storage.autoSave(state, this.state.customerId);
    }
  }

  /**
   * Save state before navigating to next step (triggered when user clicks Next)
   * This ensures Shopify save happens immediately on step completion
   */
  async saveBeforeStepCompletion() {
    const state = this.state.getState();
    const currentStep = this.state.getCurrentStep();
    const stepOrder = ['name', 'spayed', 'age', 'breed', 'weight', 'composition', 'activity', 'allergies', 'snacks', 'email', 'calculation', 'flavors', 'plan', 'bulk'];
    const nextIndex = stepOrder.indexOf(currentStep) + 1;
    const nextStep = nextIndex < stepOrder.length ? stepOrder[nextIndex] : null;

    // Early create: when leaving name step with pet name, create metaobject (no customer yet)
    if (currentStep === 'name' && state.petData?.name && !state.petMetaobjectId && this.shopifyAPI?.backendApiUrl) {
      try {
        const result = await this.shopifyAPI.createOrUpdatePetMetaobject({
          petData: state.petData,
          calculations: state.calculations || {},
          resumeStep: 'spayed'
        });
        if (result?.petMetaobjectId) {
          this.state.petMetaobjectId = result.petMetaobjectId;
        }
      } catch (e) {
        console.warn('Early pet metaobject create failed:', e.message);
      }
    }

    // Force immediate Shopify save (customer + metaobject, or metaobject-only for pre-email steps)
    await this.storage.autoSave(state, this.state.customerId, {
      forceShopifySave: true,
      nextStep,
      currentStep
    });
  }

  /**
   * Render a step
   * @param {string} step - Step identifier
   * @param {Object} params - Route parameters (optional)
   * @returns {Promise<void>}
   */
  async renderStep(step, params = {}) {
    if (this.stepHandler) {
      await this.stepHandler.renderStep(step, params);
    } else {
      console.error('Step handler not initialized');
    }
  }


  /**
   * Navigate to next step
   */
  async navigateNext() {
    const currentStep = this.state.getCurrentStep();
    
    // Validate current step before proceeding
    if (!this.state.validateStep(currentStep)) {
      console.warn(`Cannot proceed: step ${currentStep} is not valid`);
      return;
    }

    // Save state to Shopify before navigating (when user clicks Next)
    // Skip if we just created customer/pet (to avoid duplicate saves)
    if (!this.state._justCreated) {
    await this.saveBeforeStepCompletion();
    }
    
    // Reset flag after checking
    this.state._justCreated = false;

    // Check if we're leaving the name step - update title after navigation
    const isLeavingNameStep = currentStep === 'name';

    this.router.navigateNext(currentStep);

    // Update page title if we just left the name step
    // Use setTimeout to ensure state is updated after navigation
    if (isLeavingNameStep) {
      setTimeout(() => {
        this.updatePageTitle();
      }, 0);
    }
  }

  /**
   * Navigate to previous step
   */
  navigatePrevious() {
    const currentStep = this.state.getCurrentStep();
    this.router.navigatePrevious(currentStep);
  }

  /**
   * Update pet data
   * @param {string} field - Field name
   * @param {*} value - Field value
   */
  updatePetData(field, value) {
    this.state.updatePetData(field, value);
    
    // Auto-save
    this.storage.autoSave(this.state.getState(), this.state.customerId);

    // Update pending Klaviyo event if one exists (during 1-minute delay)
    if (this.klaviyoManager && this.state.petData && this.state.petData.ownerEmail) {
      try {
        this.klaviyoManager.updatePendingEvent(this.state.petData, this.state.petData.ownerEmail);
      } catch (e) {
        // Non-blocking - just log warning
        console.warn('Failed to update pending Klaviyo event:', e);
      }
    }
  }

  /**
   * Get current state
   * @returns {Object} Current state
   */
  getState() {
    return this.state.getState();
  }

  /**
   * Get flow configuration
   * @returns {Object} Flow configuration
   */
  getConfig() {
    return this.config;
  }

  /**
   * Run calculations
   * @returns {Object} Calculation results
   */
  runCalculations() {
    if (!this.calculationEngine) {
      throw new Error('Calculation engine not initialized');
    }

    const petData = this.state.getPetData();
    const results = this.calculationEngine.calculate(petData);
    
    // Update state with calculations
    this.state.updateCalculations(results);
    
    // Auto-save
    this.storage.autoSave(this.state.getState(), this.state.customerId);
    
    return results;
  }

  /**
   * Update page title based on current step
   * All steps: Show pet name if available, otherwise "Jouw hond"/"Your dog"
   * Format: "[petName] 🩵 Blue Zone" or "[Jouw hond/Your dog] 🩵 Blue Zone"
   */
  updatePageTitle() {
    // Wait a tick to ensure router has set the current step and state is loaded
    setTimeout(() => {
      // Get pet name from state
      const petData = this.state.getPetData();
      const petName = petData?.name;
      
      let titlePrefix = '';
      
      if (petName && petName.trim()) {
        titlePrefix = petName.trim();
      } else {
        titlePrefix = this.stepHandler.getLocaleString('quiz.title.quiz_steps');
      }

      // Update document title with emoji
      document.title = `${titlePrefix} 🩵 Blue Zone`;
    }, 0);
  }
}

// Export for global access
window.Wizard1Flow = Wizard1Flow;

// Initialize flow when DOM is ready (only on flow pages)
// This will be called from theme.liquid when on a flow page
window.initWizard1Flow = function() {
  // Expose flow instance globally for flavor modal access
  if (!window.__wizard1Flow) {
    window.__wizard1Flow = null;
  }
  if (window.wizard1Flow) {
    return window.wizard1Flow;
  }
  
  window.wizard1Flow = new Wizard1Flow();
  window.__wizard1Flow = window.wizard1Flow; // Expose for flavor modal access
  window.wizard1Flow.init();
  return window.wizard1Flow;
};
